import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Card, Heading, HStack, Icon, SimpleGrid, Text, VStack, chakra, Separator } from '@chakra-ui/react';
import { CAL_MULT, FAST_MATRIX, INTRO_MULT } from '@src/components/sco/Advisors/data/compCalculator';
import { useAdvisorAgreementPdfDownload } from '@src/components/sco/Advisors/hooks/useAdvisorAgreementPdfDownload';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { CalcField } from '@src/components/sco/Advisors/shared/CalcField';
import { SectionHeader } from '@src/components/sco/Advisors/shared/SectionHeader';
import { Segmented } from '@src/components/sco/Advisors/shared/Segmented';
import type { UpsertAdvisorAgreementResult } from '@src/components/sco/Advisors/types/advisorAgreement';
import { AGREEMENT_CLIFF_MONTHS_DEFAULT, AGREEMENT_STATUS_DRAFT, AGREEMENT_TERM_MONTHS_DEFAULT, AGREEMENT_VESTING_MONTHS_DEFAULT, CALIBER_LABEL_TO_KEY, COMPANY_STAGE_LABEL_TO_KEY, CONTRIBUTION_TIER_LABEL_TO_KEY, WARM_INTRO_CALC_LABEL_TO_KEY, agreementStatusTitle } from '@src/components/sco/Advisors/utils/agreementKeys';
import { marketDistributionMarkerPercent } from '@src/components/sco/Advisors/utils/compCalculatorChart';
import { canDraftFastForEngagement, currentAgreementId, fastAgreementHref, upsertInputFromEngagement } from '@src/components/sco/Advisors/utils/draftSeatAgreement';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { UPSERT_ADVISOR_AGREEMENT } from '@src/gql/mutations/UpsertAdvisorAgreement';
import { COMP_CALCULATOR_ENGAGEMENT } from '@src/gql/queries/CompCalculatorEngagement';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { LuDownload, LuFileText, LuPencil, LuSparkles } from 'react-icons/lu';
export const CompCalculatorSection = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const engagementId = typeof router.query.engagementId === 'string' ? router.query.engagementId : undefined;
  const agreementIdFromQuery = typeof router.query.agreementId === 'string' ? router.query.agreementId : undefined;
  const [stage, setStage] = useState('Pre-seed');
  const [tier, setTier] = useState('Strategic');
  const [caliber, setCaliber] = useState('Executive');
  const [intros, setIntros] = useState('None');
  const [hours, setHours] = useState(5);
  const [valuation, setValuation] = useState(8);
  const [createError, setCreateError] = useState<string | null>(null);
  const pdf = useAdvisorAgreementPdfDownload();
  const engagementQuery = useQuery<{
    advisorEngagement?: ScoAdvisorEngagement | null;
  }>(COMP_CALCULATOR_ENGAGEMENT, {
    skip: !router.isReady || !engagementId,
    variables: {
      advisorEngagementId: engagementId
    },
    fetchPolicy: 'network-only'
  });
  const engagement = engagementQuery.data?.advisorEngagement ?? null;
  const canAttachSeat = !engagementId || canDraftFastForEngagement(engagement);
  const existingAgreementId = currentAgreementId(engagement);
  const existingStatusKey = engagement?.currentAgreement?.statusKey;
  const updateDraftId = (agreementIdFromQuery && (!existingStatusKey || existingStatusKey === AGREEMENT_STATUS_DRAFT) ? agreementIdFromQuery : null) || (existingStatusKey === AGREEMENT_STATUS_DRAFT ? existingAgreementId : null);
  const openExistingOnly = !!existingAgreementId && existingStatusKey !== AGREEMENT_STATUS_DRAFT;
  const [upsertAdvisorAgreement, upsertMutation] = useMutation<UpsertAdvisorAgreementResult>(UPSERT_ADVISOR_AGREEMENT);
  const {
    eq,
    dollar,
    cash,
    median,
    low,
    high,
    markerPct
  } = useMemo(() => {
    const base = FAST_MATRIX[stage][tier];
    const equity = base * CAL_MULT[caliber] * INTRO_MULT[intros];
    const dollarVal = equity / 100 * valuation * 1_000_000;
    const cashVal = dollarVal / (hours * 24);
    const med = FAST_MATRIX[stage].Strategic;
    const lo = FAST_MATRIX[stage].Standard * 0.5;
    const hi = FAST_MATRIX[stage].Expert * 1.5;
    const pct = marketDistributionMarkerPercent({
      equity,
      low: lo,
      median: med,
      high: hi
    });
    return {
      eq: equity,
      dollar: dollarVal,
      cash: cashVal,
      median: med,
      low: lo,
      high: hi,
      markerPct: pct
    };
  }, [stage, tier, caliber, intros, hours, valuation]);
  const fmtPct = (n: number) => `${n.toFixed(2)}%`;
  const fmtMoney = (n_0: number) => `$${Math.round(n_0).toLocaleString()}`;
  const handleCreateAgreement = async () => {
    setCreateError(null);
    const companyStageKey = COMPANY_STAGE_LABEL_TO_KEY[stage];
    const contributionTierKey = CONTRIBUTION_TIER_LABEL_TO_KEY[tier];
    const caliberKey = CALIBER_LABEL_TO_KEY[caliber];
    const warmIntroKey = WARM_INTRO_CALC_LABEL_TO_KEY[intros];
    if (!companyStageKey || !contributionTierKey || !caliberKey || !warmIntroKey) {
      setCreateError('Could not map calculator inputs to agreement terms.');
      return;
    }
    if (engagementId && !canAttachSeat) {
      setCreateError('This seat cannot have a FAST agreement.');
      return;
    }
    const seatInput = engagement ? upsertInputFromEngagement(engagement) : {};
    try {
      const result = await upsertAdvisorAgreement({
        variables: {
          input: {
            ...seatInput,
            advisorAgreementId: updateDraftId,
            companyStageKey,
            contributionTierKey,
            caliberKey,
            warmIntroKey,
            equityPercent: eq,
            hoursPerMonth: hours,
            vestingMonths: AGREEMENT_VESTING_MONTHS_DEFAULT,
            cliffMonths: AGREEMENT_CLIFF_MONTHS_DEFAULT,
            termMonths: AGREEMENT_TERM_MONTHS_DEFAULT,
            cashHourlyRateInDollars: cash,
            valuationSnapshotInDollars: valuation * 1_000_000,
            fastTrackApplied: true
          }
        }
      });
      const agreementId = result.data?.upsertAdvisorAgreement?.advisorAgreement?.advisorAgreementId || null;
      if (!agreementId) {
        setCreateError('Agreement was created but no id was returned.');
        return;
      }
      await router.push(paths.agreementEdit(agreementId));
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Could not create a draft agreement.');
    }
  };
  return <Box height={'full'} overflow={{
    base: 'auto',
    lg: 'hidden'
  }} width={'full'}>
      <SimpleGrid columns={{
      base: 1,
      lg: 2
    }} gap={0} height={{
      lg: 'full'
    }} minHeight={{
      lg: 0
    }}>
        {/* inputs */}
        <Box bgColor={'colorPalette.container/50'} height={{
        lg: 'full'
      }} minHeight={{
        lg: 0
      }} overflowY={{
        base: 'visible',
        lg: 'auto'
      }}>
          <VStack align={'stretch'} gap={4} padding={4}>
            <SectionHeader eyebrow={'Manage'} title={'Compensation Calculator'} description={'Find fair compensation for an advisor. Equity for an ongoing seat, cash for a one-off expert call. Based on FAST v3 (Pre-seed / Seed / Series A × Standard / Strategic / Expert).'} />

            <Separator marginY={2} />

            {engagementId ? <Card.Root colorPalette={'purple'} size={'sm'} variant={'subtle'}>
                <Card.Body gap={1}>
                  <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                    Board seat
                  </Text>
                  {engagementQuery.loading ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
                      Loading seat…
                    </Text> : engagement ? <>
                      <Text fontWeight={'semibold'}>
                        {engagement.advisorName || engagement.advisorUser?.fullName || engagement.advisorUser?.displayName || engagement.category?.title || 'Open board seat'}
                      </Text>
                      <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                        {[engagement.category?.title, engagement.company?.name].filter(Boolean).join(' · ') || 'FAST will be linked to this seat.'}
                      </Text>
                    </> : <Text color={'red.fg'} fontSize={'sm'}>
                      That board seat could not be loaded.
                    </Text>}
                </Card.Body>
              </Card.Root> : null}

            <Card.Root size={'sm'} variant={'subtle'}>
              <Card.Body gap={4} padding={6}>
                <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
                  Our value
                </Text>
                <CalcField hint={'Earlier stage = more equity for the same help (FAST v3).'} label={'Company stage'}>
                  <Segmented options={['Pre-seed', 'Seed', 'Series A']} palette={'colorPalette'} value={stage} onChange={setStage} />
                </CalcField>
                <Separator marginY={2} />
                <CalcField hint={'To show the dollar value of the grant.'} label={'Company valuation'}>
                  <HStack border={'1px solid'} borderColor={'colorPalette.border'} borderRadius={'md'} gap={1} paddingX={3} paddingY={2}>
                    <Text fontWeight={'semibold'}>$</Text>
                    <chakra.input bg={'transparent'} color={'colorPalette.fg'} flex={1} outline={'none'} type={'number'} value={valuation} onChange={e => setValuation(Number(e.target.value) || 0)} />
                    <Text color={'colorPalette.fg/60'}>M</Text>
                  </HStack>
                </CalcField>
              </Card.Body>
            </Card.Root>

            <Card.Root size={'sm'} variant={'subtle'}>
              <Card.Body gap={4} padding={6}>
                <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
                  Advisor value
                </Text>
                <CalcField label={'Service level'} hint={tier === 'Standard' ? 'Quarterly meetings, reasonable email, and introductions (5–10 hrs/mo).' : tier === 'Strategic' ? 'Monthly meetings, recruiting, and at least one customer or investor meeting.' : 'Bi-monthly meetings, marquee introductions, and at least one strategic project.'}>
                  <Segmented options={['Standard', 'Strategic', 'Expert']} value={tier} onChange={setTier} />
                </CalcField>
                <Separator marginY={2} />
                <CalcField hint={'Track record and pull.'} label={'Advisor caliber'}>
                  <Segmented options={['Operator', 'Executive', 'Notable']} value={caliber} onChange={setCaliber} />
                </CalcField>
                <Separator marginY={2} />
                <CalcField hint={'Warm intros to investors or customers are a big value driver.'} label={'Makes intros?'}>
                  <Segmented options={['None', 'Customers', 'Investors', 'Both']} value={intros} onChange={setIntros} />
                </CalcField>
                <Separator marginY={2} />
                <CalcField hint={'FAST standard is 5–10 hours per month at every service level.'} label={`Expected time · ${hours} hrs / mo`}>
                  <chakra.input css={{
                  accentColor: 'var(--chakra-colors-orange-solid)'
                }} max={12} min={1} step={1} type={'range'} value={hours} width={'full'} onChange={e_0 => setHours(Number(e_0.target.value))} />
                </CalcField>
              </Card.Body>
            </Card.Root>
          </VStack>
        </Box>

        {/* results */}
        <Box bgColor={'colorPalette.panel'} borderColor={'colorPalette.border/30'} borderLeftWidth={{
        lg: '1px'
      }} height={{
        lg: 'full'
      }} minHeight={{
        lg: 0
      }} overflowY={{
        base: 'visible',
        lg: 'auto'
      }}>
          <VStack align={'stretch'} gap={4} marginX={'auto'} maxWidth={'2xl'} padding={{
          base: 5,
          md: 8
        }}>
            <HStack justify={'space-between'} width={'full'}>
              <Heading size={'md'}>Results</Heading>
              <Button loading={pdf.busyId === 'overview'} size={'sm'} variant={'outline'} onClick={() => {
              const companyStageKey_0 = COMPANY_STAGE_LABEL_TO_KEY[stage];
              const contributionTierKey_0 = CONTRIBUTION_TIER_LABEL_TO_KEY[tier];
              const caliberKey_0 = CALIBER_LABEL_TO_KEY[caliber];
              const warmIntroKey_0 = WARM_INTRO_CALC_LABEL_TO_KEY[intros];
              if (!companyStageKey_0 || !contributionTierKey_0 || !caliberKey_0 || !warmIntroKey_0) {
                return;
              }
              void pdf.downloadOverviewPdf({
                companyStageKey: companyStageKey_0,
                contributionTierKey: contributionTierKey_0,
                caliberKey: caliberKey_0,
                warmIntroKey: warmIntroKey_0,
                hoursPerMonth: hours,
                valuationInDollars: valuation * 1_000_000
              });
            }}>
                <LuDownload /> PDF overview
              </Button>
            </HStack>
            {pdf.error ? <Text color={'red.fg'} fontSize={'sm'}>
                {pdf.error}
              </Text> : null}

            <SimpleGrid columns={2} gap={4}>
              <Card.Root variant={'elevated'}>
                <Card.Body gap={1}>
                  <HStack justify={'space-between'}>
                    <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                      Recommended equity
                    </Text>
                    <Icon boxSize={3.5} color={'colorPalette.fg/40'}>
                      <LuPencil />
                    </Icon>
                  </HStack>
                  <Heading color={'orange.fg'} size={'3xl'}>
                    {fmtPct(eq)}
                  </Heading>
                  <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                    ≈ {fmtMoney(dollar)} · vesting 2 yrs
                  </Text>
                </Card.Body>
              </Card.Root>
              <Card.Root variant={'subtle'}>
                <Card.Body gap={1}>
                  <HStack justify={'space-between'}>
                    <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                      Per hour equivalent
                    </Text>
                    <Icon boxSize={3.5} color={'colorPalette.fg/40'}>
                      <LuPencil />
                    </Icon>
                  </HStack>
                  <Heading size={'3xl'}>{fmtMoney(cash)}</Heading>
                  <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                    Cash cost per hour
                  </Text>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>

            <Card.Root variant={'subtle'}>
              <Card.Body gap={3}>
                <Heading size={'md'}>Where this sits in the market</Heading>
                <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                  Equity grants for this profile across the market. Most cluster at the median — your
                  recommendation. The thin tails are the outliers.
                </Text>
                {/* simple distribution curve */}
                <Box height={'96px'} marginTop={2} position={'relative'}>
                  <svg height={'96'} preserveAspectRatio={'none'} viewBox={'0 0 600 96'} width={'100%'}>
                    <path d={'M0,96 C150,96 180,10 300,10 C420,10 450,96 600,96 Z'} fill={'var(--chakra-colors-purple-solid)'} opacity={0.22} />
                    <path d={'M0,96 C150,96 180,10 300,10 C420,10 450,96 600,96'} fill={'none'} opacity={0.6} stroke={'var(--chakra-colors-purple-solid)'} strokeWidth={2} />
                  </svg>
                  <Box bgColor={'orange.solid'} bottom={0} left={`${markerPct}%`} position={'absolute'} top={0} transform={'translateX(-50%)'} width={'2px'} />
                  <Badge colorPalette={'orange'} left={`${markerPct}%`} position={'absolute'} textStyle={'label2xs'} top={0} transform={'translateX(-50%)'} variant={'solid'}>
                    Your offer · {fmtPct(eq)}
                  </Badge>
                </Box>
                <HStack color={'colorPalette.fg/50'} fontSize={'2xs'} justify={'space-between'}>
                  <Text>Low · {fmtPct(low)}</Text>
                  <Text color={'colorPalette.fg/70'} fontWeight={'bold'}>
                    Median · {fmtPct(median)}
                  </Text>
                  <Text>High · {fmtPct(high)}</Text>
                </HStack>
                <SimpleGrid columns={{
                base: 1,
                sm: 2
              }} gap={3} marginTop={2}>
                  <Box>
                    <Text fontSize={'xs'} fontWeight={'bold'}>
                      Low outlier — underpaid
                    </Text>
                    <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                      Took less than the role was worth, usually because neither side knew the benchmark.
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize={'xs'} fontWeight={'bold'}>
                      High outlier — overpaid
                    </Text>
                    <Text color={'colorPalette.fg/60'} fontSize={'xs'}>
                      Celebrity names or a hot, competitive round where the advisor had real leverage.
                    </Text>
                  </Box>
                </SimpleGrid>
              </Card.Body>
            </Card.Root>

            <Card.Root bg={'colorPalette.solid/8'} colorPalette={'purple'} size={'sm'} variant={'subtle'}>
              <Card.Body gap={2} padding={6}>
                <HStack gap={2}>
                  <Icon boxSize={4} color={'colorPalette.fg/60'}>
                    <LuSparkles />
                  </Icon>
                  <Heading size={'md'}>Why this works</Heading>
                </HStack>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'md'}>
                  For a {caliber.toLowerCase()} advisor giving a {stage.toLowerCase()} company{' '}
                  {tier.toLowerCase()} help, FAST v3 starts at {fmtPct(FAST_MATRIX[stage][tier])}. We nudged
                  it to {fmtPct(eq)} for their caliber
                  {intros !== 'None' ? ` and the ${intros.toLowerCase()} intros they bring` : ''}. It vests
                  monthly over two years with a three-month cliff, and unvested shares accelerate on a sale,
                  so an advisory seat that doesn&apos;t work out costs you nothing in the first three months.
                </Text>
              </Card.Body>
            </Card.Root>

            {createError ? <Text color={'red.fg'} fontSize={'sm'}>
                {createError}
              </Text> : null}

            {openExistingOnly && existingAgreementId ? <Button asChild size={'xl'} variant={'cta'} width={'full'}>
                <NextLink href={fastAgreementHref(paths, existingAgreementId)}>
                  <LuFileText />
                  Open {agreementStatusTitle(existingStatusKey, engagement?.currentAgreement?.status?.title)}{' '}
                  FAST
                </NextLink>
              </Button> : <Button loading={upsertMutation.loading} size={'xl'} variant={'cta'} width={'full'} disabled={upsertMutation.loading || !!engagementId && (engagementQuery.loading || !canAttachSeat || !engagement)} onClick={handleCreateAgreement}>
                {updateDraftId ? 'Update FAST draft →' : 'Create FAST Agreement →'}
              </Button>}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>;
};